import { z } from 'zod';
import { STATUS_IDS } from '../../domain/combat/statuses';
import { ELEMENTS } from '../../domain/game/elements';
import { getSkillById } from '../skills/skills';

/**
 * No HP/MP potions (gameplay_ideas).
 * Combat items are free actions during the player's turn.
 */
export const itemSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  /** Can use outside combat (e.g. save crystal). */
  usableOutOfCombat: z.boolean().default(false),
  /** Can use in combat as free action. */
  usableInCombat: z.boolean().default(true),
  effect: z.discriminatedUnion('type', [
    z.object({
      type: z.literal('enemy_status'),
      statusId: z.enum(STATUS_IDS),
      turns: z.number().int().positive(),
      potency: z.number().optional(),
      element: z.enum(ELEMENTS).optional(),
    }),
    z.object({
      type: z.literal('player_status'),
      statusId: z.enum(STATUS_IDS),
      turns: z.number().int().positive(),
      potency: z.number().optional(),
      element: z.enum(ELEMENTS).optional(),
    }),
    z.object({
      type: z.literal('learn_skill'),
      skillId: z.string().min(1),
    }),
    z.object({
      type: z.literal('save_game'),
    }),
  ]),
}).superRefine((item, context) => {
  if (
    (item.effect.type === 'enemy_status' || item.effect.type === 'player_status') &&
    item.effect.statusId === 'elemental_immunity' &&
    !item.effect.element
  ) {
    context.addIssue({
      code: 'custom',
      path: ['effect', 'element'],
      message: 'Elemental immunity requires an element',
    });
  }
});

export type ItemDef = z.infer<typeof itemSchema>;

export const items: ItemDef[] = z.array(itemSchema).parse([
  {
    id: 'shock_dust',
    name: 'Bụi Choáng',
    description: 'Rắc lên kẻ địch — choáng 1 hiệp (bỏ lượt đánh).',
    usableOutOfCombat: false,
    usableInCombat: true,
    effect: { type: 'enemy_status', statusId: 'stun', turns: 1 },
  },
  {
    id: 'ember_oil',
    name: 'Dầu Tro Nóng',
    description: 'Thiêu đốt kẻ địch 2 hiệp (sát thương cuối mỗi vòng).',
    usableOutOfCombat: false,
    usableInCombat: true,
    effect: { type: 'enemy_status', statusId: 'burn', turns: 2, potency: 6 },
  },
  {
    id: 'frost_rune',
    name: 'Ấn Sương Giá',
    description: 'Đóng băng kẻ địch 1 hiệp — chắc chắn bỏ lượt.',
    usableOutOfCombat: false,
    usableInCombat: true,
    effect: { type: 'enemy_status', statusId: 'freeze', turns: 1 },
  },
  {
    id: 'nerve_needle',
    name: 'Kim Tê Liệt',
    description: 'Gây tê liệt 2 hiệp — mỗi hiệp có 50% khả năng bỏ lượt.',
    usableOutOfCombat: false,
    usableInCombat: true,
    effect: { type: 'enemy_status', statusId: 'paralysis', turns: 2, potency: 0.5 },
  },
  {
    id: 'venom_vial',
    name: 'Lọ Độc Cô Đặc',
    description: 'Gây 5 sát thương Độc mỗi hiệp trong 3 hiệp.',
    usableOutOfCombat: false,
    usableInCombat: true,
    effect: { type: 'enemy_status', statusId: 'poison', turns: 3, potency: 5 },
  },
  {
    id: 'weakening_salt',
    name: 'Muối Suy Yếu',
    description: 'Kẻ địch nhận thêm sát thương (x1.35) trong 2 hiệp.',
    usableOutOfCombat: false,
    usableInCombat: true,
    effect: { type: 'enemy_status', statusId: 'weaken', turns: 2, potency: 1.35 },
  },
  {
    id: 'battle_incense',
    name: 'Hương Chiến',
    description: 'Cường hóa đòn kế tiếp của bạn (x1.4 damage, 1 hiệp).',
    usableOutOfCombat: false,
    usableInCombat: true,
    effect: { type: 'player_status', statusId: 'power_up', turns: 1, potency: 1.4 },
  },
  {
    id: 'veil_of_ash',
    name: 'Khăn Tro',
    description: 'Bất tử 1 hiệp — đòn địch không giết được bạn.',
    usableOutOfCombat: false,
    usableInCombat: true,
    effect: { type: 'player_status', statusId: 'invulnerable', turns: 1 },
  },
  {
    id: 'flame_ward',
    name: 'Hộ Phù Kháng Hỏa',
    description: 'Miễn nhiễm sát thương Lửa trong 2 hiệp.',
    usableOutOfCombat: false,
    usableInCombat: true,
    effect: {
      type: 'player_status',
      statusId: 'elemental_immunity',
      turns: 2,
      element: 'fire',
    },
  },
  {
    id: 'tome_spark',
    name: 'Sách Phép · Lôi Tinh',
    description: 'Dùng ngoài combat để lĩnh hội Lôi Tinh nếu cùng faction.',
    usableOutOfCombat: true,
    usableInCombat: false,
    effect: { type: 'learn_skill', skillId: 'spark' },
  },
  {
    id: 'tome_tide',
    name: 'Sách Phép · Thủy Lưu',
    description: 'Dùng ngoài combat để lĩnh hội Thủy Lưu nếu cùng faction.',
    usableOutOfCombat: true,
    usableInCombat: false,
    effect: { type: 'learn_skill', skillId: 'tide' },
  },
  {
    id: 'tome_radiant_bolt',
    name: 'Sách Phép · Quang Tiễn',
    description: 'Dùng ngoài combat để lĩnh hội Quang Tiễn nếu cùng faction.',
    usableOutOfCombat: true,
    usableInCombat: false,
    effect: { type: 'learn_skill', skillId: 'radiant_bolt' },
  },
  {
    id: 'memory_crystal',
    name: 'Pha Lê Ký Ức',
    description: 'Lưu tiến trình tại bất kỳ thời điểm nào khi còn sống.',
    usableOutOfCombat: true,
    usableInCombat: true,
    effect: { type: 'save_game' },
  },
]);

for (const item of items) {
  if (item.effect.type === 'learn_skill' && !getSkillById(item.effect.skillId)) {
    throw new Error(`Item ${item.id} references unknown skill ${item.effect.skillId}`);
  }
}

export function getItemById(id: string): ItemDef | undefined {
  return items.find((i) => i.id === id);
}
